import React from "react";
import { Button, Flex, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { getTaskStatusDisplay, isTaskStatusBlocked } from "../taskStatusConfig";
import * as styles from "./styles_vanilla.css";
import type { TaskListProps } from "./types";

// Per-state row typography. The color is applied via vanilla-extract class
// (DS `<Text color={...}>` is silently ignored), and the hex values live in
// styles_vanilla.css.ts because the DS Colors palette has no exact matches.
function getRowTextStyle(status: string): {
  variant: "regular-md" | "strong-md";
  className: string;
} {
  switch (status) {
    case "COMPLETED":
      return { variant: "regular-md", className: styles.rowTextCompleted };
    case "IN_PROGRESS":
      return { variant: "strong-md", className: styles.rowTextInProgress };
    default:
      return { variant: "regular-md", className: styles.rowTextDefault };
  }
}

// Status-label color override — only IN_PROGRESS + blocked need to deviate
// from the inherited row color (mirrors getTaskStatusDisplay().textColor).
function getStatusLabelClass(status: string, blocked: boolean): string | undefined {
  if (status === "IN_PROGRESS") return styles.statusLabelInProgress;
  if (blocked) return styles.statusLabelBlocked;
  return undefined;
}

const getStepNumberClass = (status: string): string => {
  switch (status) {
    case "COMPLETED":
      return `${styles.stepNumber} ${styles.stepNumberComplete}`;
    case "IN_PROGRESS":
      return `${styles.stepNumber} ${styles.stepNumberInProgress}`;
    default:
      return `${styles.stepNumber} ${styles.stepNumberPending}`;
  }
};

// Row class: blocked rows are inert (no hover, default cursor); every other
// status is clickable and gets the hover tint + pointer cursor.
const getRowClassName = (status: string): string =>
  isTaskStatusBlocked(status)
    ? `${styles.taskTableRow} ${styles.taskTableRowMuted}`
    : `${styles.taskTableRow} ${styles.taskTableRowActive}`;

export const TaskList: React.FC<TaskListProps> = ({
  taskSteps,
  onTaskClick,
  isReadOnly = false,
  banner,
  onBulkAssignToMe,
}) => {
  const [bulkAssigning, setBulkAssigning] = React.useState(false);
  const sorted = [...taskSteps].sort((a, b) => a.stepOrder - b.stepOrder);

  const handleBulkAssignClick = async () => {
    if (!onBulkAssignToMe) return;
    setBulkAssigning(true);
    try {
      await onBulkAssignToMe();
    } catch {
      // Error feedback is handled by the mutation's onError callback in the parent
    } finally {
      setBulkAssigning(false);
    }
  };

  const emptyStateMessage = isReadOnly
    ? "This workflow is managed in Jira. Tasks and actions are not available here."
    : "No tasks found";

  return (
    <div className={styles.taskListContainer}>
      {banner}
      {sorted.length === 0 ? (
        <div className={styles.taskListCard}>
          <Flex className={styles.taskListCardHeader}>
            <Text variant="h3">Tasks</Text>
          </Flex>
          <div className={styles.taskListCardBody}>
            <Text variant="regular-md" color={Colors.Neutral600}>
              {emptyStateMessage}
            </Text>
          </div>
        </div>
      ) : (
        <div className={styles.taskListCard}>
          <Flex
            className={styles.taskListCardHeader}
            justifyContent="space-between"
            alignItems="center"
          >
            <Text variant="h3">Tasks</Text>
            {onBulkAssignToMe && (
              <Button
                variant="neutral"
                onClick={() => {
                  void handleBulkAssignClick();
                }}
                disabled={bulkAssigning}
                data-testid="task-list-bulk-assign-to-me"
              >
                {bulkAssigning ? "Assigning…" : "Assign to me"}
              </Button>
            )}
          </Flex>
          <table className={styles.taskTable}>
            <thead className={styles.taskTableHead}>
              <tr>
                <th className={styles.taskTableHeaderCell}>Name</th>
                <th className={styles.taskTableHeaderCell}>Responsible party</th>
                <th className={styles.taskTableHeaderCell}>Assigned</th>
                <th className={styles.taskTableHeaderCell} />
              </tr>
            </thead>
            <tbody>
              {sorted.map((task) => {
                const blocked = isTaskStatusBlocked(task.status);
                const display = getTaskStatusDisplay(task.status);
                const rowText = getRowTextStyle(task.status);

                // Whole row is the click target for non-blocked statuses (per
                // wireframe). Blocked rows are inert. In Jira/read-only mode
                // we suppress click handling entirely.
                const interactive = !blocked && !isReadOnly;
                const handleActivate = () => {
                  if (!interactive) return;
                  onTaskClick(String(task.id));
                };

                return (
                  <tr
                    key={task.id}
                    className={getRowClassName(task.status)}
                    data-testid={`task-row-${task.id}`}
                    role={interactive ? "button" : undefined}
                    tabIndex={interactive ? 0 : undefined}
                    aria-label={interactive ? `Open ${task.name}` : undefined}
                    aria-disabled={blocked ? true : undefined}
                    onClick={interactive ? handleActivate : undefined}
                    onKeyDown={
                      interactive
                        ? (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleActivate();
                            }
                          }
                        : undefined
                    }
                  >
                    <td className={styles.taskTableCell}>
                      <div className={styles.taskNameCell}>
                        <span className={getStepNumberClass(task.status)}>
                          {task.status === "COMPLETED" ? (
                            <Icon
                              type="check"
                              variant="outline"
                              color={Colors.Neutral0}
                              size="small"
                            />
                          ) : (
                            task.stepOrder + 1
                          )}
                        </span>
                        <Text variant={rowText.variant} className={rowText.className}>
                          {task.name}
                        </Text>
                      </div>
                    </td>

                    <td className={styles.taskTableCell}>
                      <Text variant={rowText.variant} className={rowText.className}>
                        {task.responsibleParty ?? "—"}
                      </Text>
                    </td>

                    <td className={styles.taskTableCell}>
                      <Text variant="regular-md" className={rowText.className}>
                        {task.assignee?.displayName ?? "Not assigned"}
                      </Text>
                    </td>

                    <td className={styles.taskTableCell}>
                      <div className={styles.actionCell}>
                        <span className={styles.statusFlat}>
                          {/* Per wireframe: only the lock icon (blocked rows)
                              renders alongside the label; all other statuses
                              show label-only. */}
                          {blocked && (
                            <Icon
                              type={display.icon}
                              variant="outline"
                              color={display.iconColor}
                              size="small"
                            />
                          )}
                          <Text
                            variant="regular-md"
                            color={display.textColor}
                            className={getStatusLabelClass(task.status, blocked)}
                          >
                            {display.label}
                          </Text>
                        </span>
                        {!isReadOnly && (
                          <span
                            className={
                              interactive ? styles.chevronDecorative : styles.chevronDisabled
                            }
                            aria-hidden="true"
                          >
                            <Icon
                              type="chevron-right"
                              variant="outline"
                              size="small"
                              color={interactive ? Colors.Neutral600 : Colors.Neutral400}
                            />
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TaskList;
